// Longest message we'll put in a toast. Anything past this is a traceback or a validation dump, not
// a sentence a user can act on.
const MAX_LENGTH = 200;

// Keys checked before the rest of an object, so the human-readable message wins over whatever the
// serializer happened to list first: `detail` is DRF's, `errors: [{ detail }]` is this app's older
// endpoints, `non_field_errors` is DRF's object-level validation bucket.
const PREFERRED_KEYS = ['detail', 'message', 'non_field_errors', 'errors'];

const firstMessage = (value, depth = 0) => {
  // Error payloads nest a couple of levels at most ({ field: ["msg"] }, { errors: [{ detail }] });
  // the bound just stops a cyclic or pathological body from running away.
  if (value == null || depth > 4) return null;

  if (typeof value === 'string') {
    const text = value.trim();
    // A 500 usually answers with an HTML error page — never something to show as a message.
    return !text || text.startsWith('<') ? null : text;
  }

  if (Array.isArray(value)) return value.reduce((found, item) => found ?? firstMessage(item, depth + 1), null);

  if (typeof value === 'object') {
    const keys = [...PREFERRED_KEYS, ...Object.keys(value)];
    return keys.reduce((found, key) => found ?? firstMessage(value[key], depth + 1), null);
  }

  // Numbers/booleans (status codes, flags) are never the message.
  return null;
};

/**
 * The message a failed request actually came back with, for surfaces that would otherwise toast a
 * hardcoded "Failed to …". Handles the shapes our API produces:
 *
 *   { name: ["A tag with this name already exists."] }   → the field error (DRF serializer)
 *   { detail: "Not found." }                             → DRF's APIException
 *   { errors: [{ detail: "…" }] }                        → the older JSON:API-style endpoints
 *   "Something broke"                                    → a bare string body
 *
 * Falls back when there's nothing usable — a network failure (no response at all), an HTML error
 * page, or a body with no text in it.
 *
 * `response` is what fetchConstructor returns, which for a failed request is axios' `error.response`
 * (so `response.data` is the parsed error body), or undefined if the request never got one.
 */
export const apiErrorMessage = (response, fallback) => {
  const text = firstMessage(response?.data);
  if (!text) return fallback;
  return text.length > MAX_LENGTH ? `${text.slice(0, MAX_LENGTH - 1)}…` : text;
};
